import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart,
  MessageCircle,
  UserPlus,
  Loader2,
  Star,
  Sparkles,
  X,
  HandHeart,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import FollowRequestsModal from "../Modal/FollowRequestsModal";
import {
  fetchNotifications,
  markAllNotificationsAsRead,
  respondToFollowRequest,
} from "../../services/notificationService";
import { useNotification } from "../../context/NotificationContext";
import FetchErrorState from "../Common/FetchErrorState";
import ErrorModal from "../Modal/ErrorModal";

// Helper function to get profile picture
const getProfilePicture = (profilePic) => {
  // If it's an object with sizes, use thumbnail or small
  if (profilePic && typeof profilePic === "object") {
    return profilePic.thumbnail || profilePic.small || profilePic.medium;
  }

  // If it's a string (old format), use it directly
  if (typeof profilePic === "string") {
    return profilePic;
  }

  return null;
};

const useIntersectionObserver = (callback, options = {}) => {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          callback();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
        ...options,
      },
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [callback]);

  return targetRef;
};

const NotificationList = ({ filterType = "all" }) => {
  const navigate = useNavigate();
  const { user, apiCall } = useAuth();
  const { clearNotifications } = useNotification();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [error, setError] = useState(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [followRequestsModalOpen, setFollowRequestsModalOpen] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  // Fetch notifications
  const loadNotifications = useCallback(
    async (reset = false, currentCursor = null) => {
      try {
        if (reset) {
          setLoading(true);
          setCursor(null);
        } else {
          setIsFetchingMore(true);
        }

        setError(null);

        const result = await fetchNotifications(
          apiCall,
          filterType,
          20,
          reset ? null : currentCursor, 
        );

        if (reset) {
          setNotifications(result.notifications);
        } else {
          setNotifications((prev) => [...prev, ...result.notifications]);
        }

        setHasMore(result.hasMore);
        setCursor(result.nextCursor);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    },
    [apiCall, filterType], 
  );

  // Load more handler
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isFetchingMore && !loading) {
      loadNotifications(false, cursor); 
    }
  }, [hasMore, isFetchingMore, loading, loadNotifications, cursor]);

  const loadMoreRef = useIntersectionObserver(handleLoadMore);

  // Mark all as read when component mounts
  useEffect(() => {
    const markAllAsRead = async () => {
      try {
        await markAllNotificationsAsRead(apiCall);
        clearNotifications();
      } catch (err) {

      }
    };

    // Mark as read after a short delay
    const timer = setTimeout(() => {
      markAllAsRead();
    }, 1000);

    return () => clearTimeout(timer);
  }, [apiCall, clearNotifications]);

  // Handle follow request
  const handleFollowRequest = useCallback(
    async (notificationId, action) => {
      try {
        const result = await respondToFollowRequest(
          apiCall,
          notificationId,
          action,
        );

        if (action === "accept") {
          // Update the notification in the list to show accepted status
          setNotifications((prev) =>
            prev.map((notif) =>
              notif._id === notificationId
                ? {
                    ...notif,
                    status: 1,
                  }
                : notif,
            ),
          );
        } else {
          setNotifications((prev) =>
            prev.filter((notif) => notif._id !== notificationId),
          );
        }
      } catch (err) {
        setErrorModal({
          isOpen: true,
          message: "Failed to respond to follow request. Please try again.",
        });
      }
    },
    [apiCall],
  );

  // Handle notification click
  const handleNotificationClick = useCallback(
    (notification) => {
      const { senderUsername, url } = notification;

      if (url && url !== "#") {
        navigate(url);
      } else {
        navigate(`/${senderUsername}`);
      }
    },
    [navigate, user?.profile?.username],
  );

  // Initial load and filter changes
  useEffect(() => {
    loadNotifications(true);
  }, [filterType, loadNotifications]);

  // Get icon based on notification type
  const getNotificationIcon = useCallback((type, admireType) => {
    const iconStyle = { fill: "white", padding: "0.15rem" };
    switch (type) {
      case "like":
        return <Heart className="w-5 h-5 text-white" style={iconStyle} />;
      case "comment":
      case "reply":
        return (
          <MessageCircle
            className="w-5 h-5 text-black fill-black"
            style={{ padding: "0.1rem" }}
          />
        );
      case "follow":
      case "follow_request":
        return <UserPlus className="w-5 h-5 text-black" />;
      case "admire":
        switch (admireType) {
          case "inspiration":
            return <Star className="w-5 h-5 text-white" style={iconStyle} />;
          case "adore":
            return <Heart className="w-5 h-5 text-white" style={iconStyle} />;
          case "support":
            return (
              <HandHeart className="w-5 h-5 text-white" style={iconStyle} />
            );
          case "idol":
            return (
              <Sparkles className="w-5 h-5 text-white" style={iconStyle} />
            );
          default:
            return <Heart className="w-5 h-5 text-white" style={iconStyle} />;
        }
      default:
        return <Heart className="w-5 h-5 text-black" />;
    }
  }, []);

  // Get notification text
  const getNotificationText = useCallback((notification) => {
    const { type, senderUsername, commentText, admireType, status } =
      notification;

    switch (type) {
      case "like":
        // Check if it's a post like or comment like
        if (notification.commentText) {
          return (
            <>
              <span className="font-semibold text-black">{senderUsername}</span>{" "}
              liked your comment: {commentText}
            </>
          );
        } else {
          return (
            <>
              <span className="font-semibold text-black">{senderUsername}</span>{" "}
              liked your post
            </>
          );
        }
      case "comment":
        return (
          <>
            <span className="font-semibold text-black">{senderUsername}</span>{" "}
            commented: {commentText}
          </>
        );
      case "reply":
        return (
          <>
            <span className="font-semibold text-black">{senderUsername}</span>{" "}
            replied: {commentText}
          </>
        );
      case "follow":
        return (
          <>
            <span className="font-semibold text-black">{senderUsername}</span>{" "}
            started following you
          </>
        );
      case "follow_request":
        if (status === 1) {
          return (
            <>
              You accepted{" "}
              <span className="font-semibold text-black">{senderUsername}</span>
              's follow request
            </>
          );
        }
        return (
          <>
            <span className="font-semibold text-black">{senderUsername}</span>{" "}
            requested to follow you
          </>
        );
      case "admire":
        const iconClass = "w-4 h-4 inline-block text-black"; // Black outline class
        const iconProps = {
          strokeWidth: 2.5, // Makes the outline bolder
          className: iconClass,
        };

        const admireMessages = {
          inspiration: (
            <>
              finds you inspiring <Star {...iconProps} />
            </>
          ),
          idol: (
            <>
              admires you as an idol <Sparkles {...iconProps} />
            </>
          ),
          adore: (
            <>
              adores your work <Heart {...iconProps} />
            </>
          ),
          support: (
            <>
              supports you <HandHeart {...iconProps} />
            </>
          ),
        };
        return (
          <>
            <span className="font-semibold text-black">{senderUsername}</span>{" "}
            {admireMessages[admireType] || "admires you"}
          </>
        );
      default:
        return (
          <span className="font-semibold text-black">{senderUsername}</span>
        );
    }
  }, []);

  // Format time ago
  const formatTimeAgo = useCallback((date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
    return `${Math.floor(seconds / 604800)}w`;
  }, []);

  if (loading && notifications.length === 0) {
    return (
      <div className="flex justify-center items-center py-12 bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error && notifications.length === 0) {
    return (
      <div className="bg-white">
        <FetchErrorState
          message={error}
          onRetry={() => loadNotifications(true)}
        />
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Header */}
      {/* <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="flex items-center justify-center p-4 relative">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-black" />
          </button>

          <h1 className="text-xl font-semibold text-black">Notifications</h1>
        </div>
      </div> */}

      {/* Follow Requests Section - Always visible */}
      <div className="border-b border-gray-200">
        <button
          onClick={() => setFollowRequestsModalOpen(true)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#b0d284] flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-black" />
            </div>

            <div className="text-left">
              <h3 className="font-semibold text-black">Follow Requests</h3>
              <p className="text-sm text-gray-600">
                Approve or ignore requests
              </p>
            </div>
          </div>
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <div className="text-center py-12 text-black">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-black mb-2">
            No notifications yet
          </h3>
          <p className="text-gray-600">
            When someone likes, comments, or admires you, you'll see it here.
          </p>
        </div>
      ) : (
        <div>
          {notifications.map((notification) => (
            <div
              key={notification._id}
              className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Sender Avatar */}
                <div className="relative flex-shrink-0">
                  {(() => {
                    const profilePictureUrl = getProfilePicture(
                      notification.senderProfilePic,
                    );
                    return profilePictureUrl ? (
                      <img
                        src={profilePictureUrl}
                        alt={notification.senderUsername}
                        className="w-12 h-12 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border border-gray-200">
                        <span className="text-slate-600 font-semibold text-sm">
                          {notification.senderName
                            .trim()
                            .split(/\s+/)
                            .slice(0, 2)
                            .map((word) => word.charAt(0).toUpperCase())
                            .join("")}
                        </span>
                      </div>
                    );
                  })()}

                  {/* Notification Badge on Avatar - Show for like, comment, reply, and admire */}
                  {/* Notification Badge on Avatar */}
                  {(notification.type === "like" ||
                    notification.type === "comment" ||
                    notification.type === "reply" ||
                    notification.type === "admire") && (
                    <div
                      className={`absolute bottom-0 right-0 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center ${
                        notification.type === "like" ||
                        notification.type === "admire"
                          ? "bg-red-500" // Red circle for Likes and all Admire types
                          : "bg-white border-gray-200"
                      }`}
                    >
                      {getNotificationIcon(
                        notification.type,
                        notification.admireType,
                      )}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div
                    onClick={() => handleNotificationClick(notification)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start gap-2">
                      {/* Notification Icon */}
                      <p className="text-sm text-gray-900 flex-1">
                        {getNotificationText(notification)}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTimeAgo(notification.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Follow Request Buttons - Aligned to the right */}
                {notification.type === "follow_request" &&
                  notification.status === 0 && (
                    <div className="flex gap-2 ml-2 flex-shrink-0">
                      <button
                        onClick={() =>
                          handleFollowRequest(notification._id, "accept")
                        }
                        className="px-3 py-1.5 bg-[#b0d284] text-black text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() =>
                          handleFollowRequest(notification._id, "reject")
                        }
                        className="p-1.5 text-black rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center"
                        title="Reject"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                {/* Status indicator for rejected requests */}
                {notification.type === "follow_request" &&
                  notification.status === 2 && (
                    <div className="flex-shrink-0">
                      <p className="text-xs text-gray-500 font-medium">
                        Rejected
                      </p>
                    </div>
                  )}

                {/* Post Thumbnail */}
                {notification.postThumbnail && (
                  <img
                    src={notification.postThumbnail}
                    alt="Post"
                    className="w-12 h-12 rounded object-cover flex-shrink-0 cursor-pointer border border-gray-200"
                    onClick={() => handleNotificationClick(notification)}
                  />
                )}
              </div>
            </div>
          ))}

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <div
              ref={loadMoreRef}
              className="p-4 flex items-center justify-center"
            >
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
              <span className="text-sm text-gray-500">Loading more...</span>
            </div>
          )}

          {/* End of List */}
          {!hasMore && notifications.length > 0 && (
            <div className="p-4 text-center text-sm text-gray-500">
              You're all caught up!
            </div>
          )}
        </div>
      )}
      {/* Follow Requests Modal */}
      <FollowRequestsModal
        open={followRequestsModalOpen}
        onClose={() => {
          setFollowRequestsModalOpen(false);
          loadNotifications(true); // Refresh main notification list
        }}
      />

      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </div>
  );
};

export default NotificationList;
